<template>
  <div :class="`card pt-2 ${cardClasses}`">
    <div class="card-header border-0">
      <div class="card-title">
        <h2>Invoices</h2>
      </div>
      <div class="card-toolbar m-0">
        <ul class="nav nav-stretch fs-5 fw-semobold nav-line-tabs nav-line-tabs-2x border-transparent" role="tablist">
          <li class="nav-item" role="presentation">
            <a href="#" @click.prevent="getDataByYear(0)" class="nav-link text-active-primary"
               :class="{ active: selectedYear === 0 }"
               data-bs-toggle="tab" role="tab">
              All
            </a>
          </li>
          <li v-for="(item, index) in 3" :key="index" class="nav-item" role="presentation">
            <a v-if="item === 1" href="#" @click.prevent="getDataByYear(new Date().getFullYear())" class="nav-link text-active-primary"
               :class="{ active: selectedYear === new Date().getFullYear() }"
               data-bs-toggle="tab" role="tab">
              This Year 
            </a>
            <a v-else href="#" @click.prevent="getDataByYear(new Date().getFullYear() + 1 - item)" class="nav-link text-active-primary"
               :class="{ active: selectedYear === new Date().getFullYear() + 1 - item }"
               data-bs-toggle="tab" role="tab">
              {{new Date().getFullYear()+1 - item}}
            </a>
          </li>
        </ul>
      </div>
    </div>
    <div class="card-body pt-0">
      <div v-if="loadError" class="alert alert-danger d-flex justify-content-between align-items-center">
        <span>{{ loadError }}</span>
        <button class="btn btn-sm btn-light-danger" @click="getDataByYear(selectedYear)">Retry</button>
      </div>
      <div v-else-if="isLoading" class="d-flex justify-content-center py-10">
        <span class="spinner-border spinner-border-sm me-3"></span>
        Loading invoices...
      </div>
      <div id="kt_referred_users_tab_content" class="tab-content">
        <div v-if="!isLoading && !loadError" class="py-0 tab-pane fade active show" role="tabpanel" >
          <Datatable 
              :table-header="tableHeader"
              :table-data="data"
              :rows-per-page="5"
              :enable-items-per-page-dropdown="false" >
            <template v-slot:cell-IssueDate="{ row: invoice }">
              {{ dateFormat(invoice.IssueDate) }}
            </template>
            <template v-slot:cell-DueDate="{ row: invoice }">
              {{ dateFormat(invoice.DueDate) }}
            </template>
            <template v-slot:cell-TotalAmount="{ row: invoice }">
              {{ currencyFormat(invoice.TotalAmount, invoice.Currency) }}
            </template>
            <template v-slot:cell-Currency="{ row: invoice }">
              {{ invoice.Currency }}
            </template>
            <template v-slot:cell-CurrencyRate="{ row: invoice }">
              {{ invoice.CurrencyRate }}
            </template>
            <template v-slot:cell-Status="{ row: invoice }">
              {{
                invoice.Status
              }}
            </template>
          </Datatable>
          <div v-if="data.length === 0" class="text-center text-muted py-8">
            <span v-if="selectedYear === 0">No invoices were found for this vendor.</span>
            <span v-else>No invoices were found for this vendor in {{ selectedYear }}.</span>
          </div>
        </div>
        <div id="kt_customer_details_invoices_4" class="py-0 tab-pane fade" role="tabpanel">
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent, ref, watch} from "vue";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import {api} from "@/clients/ApiClientService";
import {ApiError, InvoiceVM} from "../../../clients/ApiClient";
import {dateOnlyFormat as dateFormat} from "@/extensions/dateExtension";
import {currencyFormat} from "@/extensions/currencyExtension";
export default defineComponent({
  name: "invoices-table",
  props: {
    vendorId: {
      type: String,
      required: true
    },
    cardClasses: String,
  },
  components: {
    Datatable,
  },
  setup(props) {
    const tableHeader = ref([
      {
        name: "Issue Date",
        key: "IssueDate",
        sortable: true,
      },
      {
        name: "Due Date",
        key: "DueDate",
        sortable: true,
      },
      {
        name: "Total Amount",
        key: "TotalAmount",
        sortable: true,
      },
      {
        name: "Currency",
        key: "Currency",
        sortable: true,
      },
      {
        name: "Currency Rate",
        key: "CurrencyRate",
        sortable: true,
      },
      {
        name: "Status",
        key: "Status",
        sortable: true,
        align: 'right',
      },
      // {
      //   name: "Status",
      //   key: "status",
      //   sortingField: "status.label",
      //   sortable: true,
      // },
    ]);
    const data = ref<InvoiceVM[]>([]);
    const selectedYear = ref(0);
    const isLoading = ref(false);
    const loadError = ref("");
    let requestSequence = 0;

    watch(
      () => props.vendorId,
      async (vendorId) => {
        data.value = [];
        if (vendorId) {
          await getDataByYear(selectedYear.value);
        }
      },
      { immediate: true }
    );
    
    async function getDataByYear (year:number){
      const vendorId = props.vendorId;
      const currentRequest = ++requestSequence;
      selectedYear.value = year;
      data.value = [];
      loadError.value = "";
      isLoading.value = true;

      try {
        const response = await api.invoices.getInvoicesByVendorAndYear(vendorId, year);
        if (currentRequest === requestSequence && vendorId === props.vendorId) {
          data.value = response.Invoices ?? [];
        }
      } catch (error) {
        if (currentRequest === requestSequence && vendorId === props.vendorId) {
          loadError.value = invoiceLoadErrorMessage(error as ApiError);
        }
      } finally {
        if (currentRequest === requestSequence && vendorId === props.vendorId) {
          isLoading.value = false;
        }
      }
    }
    return { tableHeader, data, selectedYear, isLoading, loadError, dateFormat, currencyFormat, getDataByYear };
  },
});

function invoiceLoadErrorMessage(error: ApiError): string {
  if (error.status === 401) {
    return "Your session could not load these invoices. Sign out and sign in again, then retry.";
  }
  if (error.status === 403) {
    return "Your account is not authorized to view invoices for this company.";
  }
  return "The vendor invoices could not be loaded. Retry, and contact support if the problem continues.";
}
</script>
